/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react';
import { IconSearch, IconPlus } from '@tabler/icons-react';
import { IconParent, IconTeacher, IconSchool } from '../../../../assets/icons';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Constants, classNames, getStatusColor } from '../../../../utils';
import { AdminReportsService } from '../../../../services';
import { generateSchoolPDF, generateTeacherPDF, generateParentPDF } from './pdf-reports';


// 3,4,5
//prof, school, parent

const MODAL_ACTION = {
  NONE: 0,
  UPDATE_STATUS: 1,
  VIEW: 3
}

const planStatus = (user) => {
  const plan = user?.last_plan_user;
  if (!plan || !plan.next_payment) return "Sin suscripción";
  return moment(plan.next_payment).isBefore(moment(), 'day') ? "Vencida" : "Vigente";
};

const planDateExpired = (user) => {
  const plan = user?.last_plan_user;
  if (!plan || !plan.next_payment) return "-";
  return moment(plan.next_payment).format('DD-MM-YYYY');
};

// Asume que usas Tailwind CSS, si no, usa style={{ color: ... }} abajo
const planStatusColor = (user) => {
  const status = planStatus(user);
  if (status === "Vencida") return 'text-danger';
  if (status === "Vigente") return 'text-green';
  return 'text-black';
};

const AdminUsersReports = () => {
  const [selectedItem, setSelectedItem] = useState();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const { canResetFilter, data, filterBy, goToPage, isLoading, pagination, reload, filters, update } = useFetchTable();

  const onSelectItem = (item, action) => {
    setSelectedItem(item);
    setModalAction(action);
  };

  const closeModal = (reloading = false) => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
    if (reloading) reload(true);
  };

  const generatePDF = () => {
    if (filters.level_id === Constants.USER.LEVELS.PROFESSOR) {
      generateTeacherPDF(data);
      return;
    }
    if (filters.level_id === Constants.USER.LEVELS.SCHOOL) {
      generateSchoolPDF(data);
      return;
    }
    if (filters.level_id === Constants.USER.LEVELS.PARENT) {
      generateParentPDF(data);
      return;
    }
  };

  return (
    <div>
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Modal
        size="sm"
        isOpen={modalAction !== MODAL_ACTION.NONE}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            const isTeacher = filters.level_id === Constants.USER.LEVELS.PROFESSOR;

            if (modalAction === MODAL_ACTION.VIEW && isTeacher) return (
              <ModalViewSubjects item={selectedItem} />
            );

            if (modalAction === MODAL_ACTION.UPDATE_STATUS) return (
              <ModalUpdateStatus
                user={selectedItem}
                onClose={onClose}
                onUpdate={() => {
                  update(selectedItem);
                  closeModal();
                }}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <Filters
        canResetFilter={canResetFilter}
        filterBy={filterBy}
        resetFilter={() => reload()}
        currentLevel={filters.level_id}
      />

      {filters.level_id === Constants.USER.LEVELS.SCHOOL &&
        <SchoolTable
          data={data}
          onSelectItem={onSelectItem}
          generatePDF={generatePDF}
        />
      }

      {filters.level_id === Constants.USER.LEVELS.PROFESSOR &&
        <TeacherTable
          data={data}
          onSelectItem={onSelectItem}
          generatePDF={generatePDF}
        />
      }

      {filters.level_id === Constants.USER.LEVELS.PARENT &&
        <ParentTable
          data={data}
          onSelectItem={onSelectItem}
          generatePDF={generatePDF}
        />
      }

      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}

const SchoolTable = ({ data, onSelectItem, generatePDF }) => {
  return (
    <Table
      aria-label="Años Escolares"
      color="primary"
      topContentPlacement="inside"
      topContent={
        <div className="flex flex-row justify-between items-center gap-4">
          <h3 className="text-xl font-medium">Centros educativos registrados</h3>
          <Button
            className="pr-2 bg-green font-medium text-white"
            endContent={<IconPlus color="white" />}
            onClick={generatePDF}
          >
            Descargar en PDF
          </Button>
        </div>
      }
    >
      <TableHeader>
        <TableColumn>Fecha de registro</TableColumn>
        <TableColumn>Nombre</TableColumn>
        <TableColumn>ID del CE</TableColumn>
        <TableColumn>Nombre del director</TableColumn>
        <TableColumn>Teléfono institucional</TableColumn>
        <TableColumn>Correo</TableColumn>
        <TableColumn>Estatus Suscripción</TableColumn>
        <TableColumn>Fecha de Vencimiento</TableColumn>
        <TableColumn>Estatus</TableColumn>
      </TableHeader>
      <TableBody items={data} emptyContent="No hay colegios registrados">
        {(user) => (
          <TableRow key={user.id}>
            <TableCell>{moment(user?.created_at).format('DD-MM-YYYY')}</TableCell>
            <TableCell>{user?.fullname}</TableCell>
            <TableCell>{user?.dea_code}</TableCell>
            <TableCell>{user?.director}</TableCell>
            <TableCell>{user?.phonecode?.name}{user?.phone}</TableCell>
            <TableCell>{user?.email}</TableCell>
            <TableCell>
              <span className={planStatusColor(user)}>
                {planStatus(user)}
              </span>
            </TableCell>
            <TableCell>{planDateExpired(user)}</TableCell>
            <TableCell>
              <span
                onClick={() => onSelectItem({ ...user, status: user.status ? 0 : 1 }, MODAL_ACTION.UPDATE_STATUS)}
                className={classNames('font-medium cursor-pointer', getStatusColor(user.status))}
              >
                {user.status_text}
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

const TeacherTable = ({ data, onSelectItem, generatePDF }) => {
  return (
    <Table
      aria-label="Años Escolares"
      color="primary"
      topContentPlacement="inside"
      topContent={
        <div className="flex flex-row justify-between items-center gap-4">
          <h3 className="text-xl font-medium">Profesores registrados</h3>
          <Button
            className="pr-2 bg-green font-medium text-white"
            endContent={<IconPlus color="white" />}
            onClick={generatePDF}
          >
            Descargar en PDF
          </Button>
        </div>
      }
    >
      <TableHeader>
        <TableColumn>Fecha de registro</TableColumn>
        <TableColumn>Nombre</TableColumn>
        <TableColumn>DNI/C.I</TableColumn>
        <TableColumn>Teléfono</TableColumn>
        <TableColumn>Correo</TableColumn>
        <TableColumn>País</TableColumn>
        <TableColumn>CE donde labora</TableColumn>
        <TableColumn>Materias que dicta</TableColumn>
        <TableColumn>Estatus Suscripción</TableColumn>
        <TableColumn>Fecha de Vencimiento</TableColumn>
        <TableColumn>Estatus</TableColumn>
      </TableHeader>
      <TableBody items={data} emptyContent="No hay profesores registrados">
        {(user) => (
          <TableRow key={user.id}>
            <TableCell>{moment(user?.created_at).format('DD-MM-YYYY')}</TableCell>
            <TableCell>{user?.fullname}</TableCell>
            <TableCell>{user?.document_type?.name}{user?.document}</TableCell>
            <TableCell>{user?.phonecode?.name}{user?.phone}</TableCell>
            <TableCell>{user?.email}</TableCell>
            <TableCell>{user?.country?.name}</TableCell>
            <TableCell>{user?.school?.name || user?.educational_center}</TableCell>
            <TableCell>
              {!!user?.subjects?.length && (
                <span
                  className="cursor-pointer font-semibold text-sm text-secondary"
                  onClick={() => onSelectItem(user, MODAL_ACTION.VIEW)}
                >
                  Ver
                </span>
              )}
            </TableCell>
            <TableCell>
              <span className={planStatusColor(user)}>
                {planStatus(user)}
              </span>
            </TableCell>
            <TableCell>{planDateExpired(user)}</TableCell>
            <TableCell>
              <span
                onClick={() => onSelectItem({ ...user, status: user.status ? 0 : 1 }, MODAL_ACTION.UPDATE_STATUS)}
                className={classNames('font-medium cursor-pointer', getStatusColor(user.status))}
              >
                {user.status_text}
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}


const ParentTable = ({ data, onSelectItem, generatePDF }) => {
  return (
    <Table
      aria-label="Años Escolares"
      color="primary"
      topContentPlacement="inside"
      topContent={
        <div className="flex flex-row justify-between items-center gap-4">
          <h3 className="text-xl font-medium">Padres o representantes registrados</h3>
          <Button
            className="pr-2 bg-green font-medium text-white"
            endContent={<IconPlus color="white" />}
            onClick={generatePDF}
          >
            Descargar en PDF
          </Button>
        </div>
      }
    >
      <TableHeader>
        <TableColumn>Nombre</TableColumn>
        <TableColumn>DNI/C.I</TableColumn>
        <TableColumn>Teléfono</TableColumn>
        <TableColumn>Correo</TableColumn>
        <TableColumn>Genero</TableColumn>
        <TableColumn>País</TableColumn>
        <TableColumn>Fecha de nacimiento</TableColumn>
        <TableColumn>Estado civil</TableColumn>
        <TableColumn>Representados</TableColumn>
        <TableColumn>Estatus</TableColumn>
      </TableHeader>
      <TableBody items={data} emptyContent="No hay padres o representantes registrados">
        {(user) => {
          return (
            <TableRow key={user.id}>
              <TableCell>{user?.fullname}</TableCell>
              <TableCell>{user?.document_type?.name}{user?.document}</TableCell>
              <TableCell>{user?.phonecode?.name}{user?.phone}</TableCell>
              <TableCell>{user?.email}</TableCell>
              <TableCell>{user?.gender?.name}</TableCell>
              <TableCell>{user?.country?.name}</TableCell>
              <TableCell>{moment(user?.birthdate).format('DD/MM/YYYY')}</TableCell>
              <TableCell>{user?.civil_status?.name}</TableCell>
              <TableCell>
                {user?.represented?.map(item => (
                  <p key={item.id} className="font-semibold text-sm text-secondary mb-0">
                    {item?.student?.fullname}
                  </p>
                ))}
              </TableCell>
              <TableCell>
                <span
                  onClick={() => onSelectItem({ ...user, status: user.status ? 0 : 1 }, MODAL_ACTION.UPDATE_STATUS)}
                  className={classNames('font-medium cursor-pointer', getStatusColor(user.status))}
                >
                  {user.status_text}
                </span>
              </TableCell>
            </TableRow>
          )
        }}
      </TableBody>
    </Table>
  )
}


const ModalUpdateStatus = ({ user, onClose, onUpdate }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Confirmación</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Estás seguro de cambiar el estatus?</p>
        <p className="text-center font-medium">{user.fullname}</p>
      </ModalBody>
      <ModalFooter className="justify-evenly">
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button className='bg-primary text-white' onPress={onUpdate}>Aceptar</Button>
      </ModalFooter>
    </>
  )
}

const ModalViewSubjects = ({ item }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Materias que dicta</ModalHeader>
      <ModalBody className="p-0 pb-4">
        <Table aria-label="materias" removeWrapper isStriped classNames={{ th: '!rounded-none' }}>
          <TableHeader>
            <TableColumn>Nombre</TableColumn>
          </TableHeader>
          <TableBody items={item.subjects ?? []} emptyContent="No hay materias">
            {(subject) => (
              <TableRow key={subject.id}>
                <TableCell>{subject?.subject?.name}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ModalBody>
    </>
  )
}

const Filters = ({ canResetFilter, filterBy, resetFilter, currentLevel }) => {
  const initialFilter = {
    search: '',
    dea_code: '',
    since: '',
    until: '',
    level_id: Constants.USER.LEVELS.SCHOOL
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }

  const labelText = useMemo(() => {
    switch (currentLevel) {
      case Constants.USER.LEVELS.SCHOOL:
        return 'Nombre del centro educativo';
      case Constants.USER.LEVELS.PROFESSOR:
        return 'Nombre o documento de usuario';
      case Constants.USER.LEVELS.PARENT:
        return 'Nombre o documento de usuario';
      default:
        return 'Buscar';
    }
  }, [currentLevel])

  return (
    <section className="mb-4">
      <div className='grid sm:flex grid-cols-1 lg:flex-row items-end gap-4'>
        <Input
          classNames={{
            base: 'w-full sm:max-w-[15rem]',
            inputWrapper: 'border-1 h-10',
          }}
          label={labelText}
          labelPlacement="outside"
          placeholder="Buscar"
          startContent={<IconSearch />}
          variant="bordered"
          radius="sm"
          value={form.search}
          onValueChange={v => onChange(v, 'search')}
        />

        {currentLevel === Constants.USER.LEVELS.SCHOOL && (
          <Input
            classNames={{
              base: 'w-full sm:max-w-[15rem]',
              inputWrapper: 'border-1 h-10',
            }}
            label="ID del CE"
            labelPlacement="outside"
            placeholder="ID del CE"
            variant="bordered"
            radius="sm"
            value={form.dea_code}
            onValueChange={v => onChange(v, 'dea_code')}
          />
        )}

        <Input
          type="date"
          classNames={{
            base: 'w-full sm:max-w-[10rem]',
            inputWrapper: 'border-1 h-10',
            input: `pr-0 text-${!!form.since ? '[]' : 'foreground-400'}`,
          }}
          label="Desde"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          value={form.since}
          onValueChange={v => onChange(v, 'since')}
        />
        <Input
          type="date"
          classNames={{
            base: 'w-full md:max-w-[10rem]',
            inputWrapper: 'border-1 h-10',
            input: `pr-0 text-${!!form.until ? '[]' : 'foreground-400'}`,
          }}
          label="Hasta"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          value={form.until}
          onValueChange={v => onChange(v, 'until')}
        />

        {canResetFilter && (
          <Button
            variant="light"
            className="text-primaryDark"
            onClick={() => {
              setForm(initialFilter);
              resetFilter();
            }}
          >
            Limpiar filtros
          </Button>
        )}
      </div>
      <div className='grid sm:flex grid-cols-2 lg:flex-row items-end gap-4 mt-4'>
        <UserLevelButton
          level={Constants.USER.LEVELS.SCHOOL}
          form={form}
          onChange={onChange}
          icon={IconSchool}
          label={
            <div className="flex flex-col items-start leading-4">
              <span>Centros</span>
              <span>Educativos</span>
            </div>
          }
        />
        <UserLevelButton
          level={Constants.USER.LEVELS.PROFESSOR}
          form={form}
          onChange={onChange}
          icon={IconTeacher}
          label="Profesores"
        />
        <UserLevelButton
          level={Constants.USER.LEVELS.PARENT}
          form={form}
          onChange={onChange}
          icon={IconParent}
          label={
            <div className="flex flex-col items-start leading-4">
              <span>Padres o</span>
              <span>Representantes</span>
            </div>
          }
        />
      </div>
    </section>
  )
}

const UserLevelButton = ({ level, form, onChange, icon, label }) => {
  return (
    <Button
      className={classNames(
        "w-full md:max-w-[10rem]",
        form.level_id === level ? "bg-green border-green text-white" : "bg-white"
      )}
      variant="bordered"
      radius="sm"
      onClick={() => onChange(level, "level_id")}
      startContent={
        <img
          src={icon}
          className={form.level_id === level ? "invert brightness-0" : ""}
          alt=""
        />
      }
    >
      {label}
    </Button>
  );
};

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    dea_code: '',
    since: '',
    until: '',
    level_id: Constants.USER.LEVELS.SCHOOL
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const debounceTime = 500;
  const debounce = useRef();

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const response = await AdminReportsService.adminReports.getUsers(filters)
      const { data, ...rest } = response;

      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const update = async (item) => {
    if (!canFetch) return;
    setCanFetch(false);
    try {
      await AdminReportsService.adminReports.updateUser(item);
      toast.success('Usuario actualizado con éxito');
      fetchData();
    } catch (error) {
      onError(String(error));
    } finally {
      setCanFetch(true);
    }
  }


  const getCurrentPagination = () => {
    // Truco para obtener el estado actualizado (pagination es mantenida con el estado actual por el componente Pagination)
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const canResetFilter = () => {
    const { page, perPage, ...initial } = initialFilters;
    const { page: _, perPage: __, ...current } = filters;
    delete initial.level_id;
    delete current.level_id;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    else fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  return {
    canResetFilter: canResetFilter(),
    changePerPage,
    data,
    filters,
    filterBy,
    goToPage,
    isLoading: !canFetch,
    pagination,
    reload,
    update,
  }
}

export default AdminUsersReports;
